import BasicModal from 'components/Modals/presets/BasicModal';
import Select from 'components/Inputs/Select';
import Switch from 'components/Kizen/Switch';
import Section from 'components/Wizards/shared/components/section';
import { StyledWizardField } from 'components/Wizards/shared/styles';
import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MODES,
  MODE_TO_BLOCK_SURFACE,
} from 'components/DashboardGrid/hooks/useDashboardChildren';
import { PluginContext } from 'ts-components/Plugins/context';

const PluginBlockWizard = ({
  plugin: pluginProp,
  dashlet,
  mode,
  onHide,
  onConfirm,
  initialBlock = null,
  initialDropShadow = false,
}) => {
  const { t } = useTranslation();
  const { pluginBlocks } = useContext(PluginContext);

  // Edit mode: resolve plugin entry and initial values from the dashlet config.
  const plugin =
    pluginProp ??
    pluginBlocks.find(
      (p) => p.plugin_api_name === dashlet?.config?.pluginApiName
    );

  const resolvedInitialBlock = useMemo(() => {
    if (!dashlet || !plugin) return initialBlock;
    const block = plugin.blocks?.find(
      (b) => b.api_name === dashlet.config.blockApiName
    );
    return block ? { value: block.api_name, label: block.name, block } : null;
  }, [dashlet, plugin, initialBlock]);

  const resolvedInitialDropShadow = dashlet
    ? dashlet.config.feExtraInfo?.dashletStyleConfig?.dropShadow ?? false
    : initialDropShadow;

  const [selectedBlock, setSelectedBlock] = useState(resolvedInitialBlock);
  const [dropShadow, setDropShadow] = useState(resolvedInitialDropShadow);

  const surface =
    MODE_TO_BLOCK_SURFACE[mode] ?? MODE_TO_BLOCK_SURFACE[MODES.HOMEPAGES];

  const blockOptions = useMemo(() => {
    return (plugin?.blocks ?? [])
      .filter((block) => {
        if (!block.types?.length) return true;
        return block.types.includes(surface);
      })
      .map((block) => ({ value: block.api_name, label: block.name, block }));
  }, [plugin, surface]);

  if (!plugin) return null;

  const isEditing = Boolean(dashlet);

  const handleConfirm = () => {
    onConfirm({
      pluginApiName: plugin.plugin_api_name,
      blockApiName: selectedBlock?.value,
      dropShadow,
      minW: selectedBlock?.block?.min_w,
      maxW: selectedBlock?.block?.max_w,
      minH: selectedBlock?.block?.min_h,
      maxH: selectedBlock?.block?.max_h,
    });
  };

  const blockChosen = Boolean(selectedBlock);

  return (
    <BasicModal
      heading={
        plugin?.plugin_name ??
        (isEditing ? t('Edit Plugin Block') : t('Add Plugin Block'))
      }
      buttonText={isEditing ? t('Save') : t('Add')}
      defaultLeftBtnText={t('Cancel')}
      actionBtnColor="green"
      disabled={!blockChosen}
      onConfirm={handleConfirm}
      onHide={onHide}
      show
      size="medium"
      typeOfContent="new-wizard"
      enforceFocus={false}
      autoFocus={false}
      restoreFocus={false}
    >
      <Section active done={blockChosen} index={0} title={t('Choose Block')}>
        <Select
          className="modal-select"
          options={blockOptions}
          value={selectedBlock}
          onChange={setSelectedBlock}
          placeholder={t('Select a block...')}
          data-qa-id="plugin-block-wizard-choose-block"
          portal
          inModal
          captureMenuScroll={false}
        />
      </Section>
      <Section
        active={blockChosen}
        done={false}
        index={1}
        title={t('Drop Shadow')}
        last
      >
        <StyledWizardField top={8}>
          <Switch
            checked={dropShadow}
            onChange={(e) => setDropShadow(e.target.checked)}
            label={t('Enable drop shadow')}
            data-qa-id="plugin-block-wizard-drop-shadow"
          />
        </StyledWizardField>
      </Section>
    </BasicModal>
  );
};

export default PluginBlockWizard;
